import { useLocation, useNavigate } from '@tanstack/react-router'
import { AnimatePresence, LayoutGroup, m } from 'motion/react'
import {
  type PropsWithChildren,
  createContext,
  use,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { isShallowEqual, last } from 'remeda'
import { usePrevious, useTitle } from '~/hooks'

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Heading1,
  cn,
} from '@hap/components'
import { VOID_FUNC } from '@hap/constants'

/** A title segment can be a plain string or an object with a label and optional click handler. */
export type TitleSegment =
  | string
  | { label: string; description?: string; onClick?: () => void }

/** Resolve a TitleSegment to its display label. */
function segmentLabel(segment: TitleSegment): string {
  return typeof segment === 'string' ? segment : segment.label
}

/** Stable comparison key for segments — only compares labels. */
function segmentLabels(segments: TitleSegment[]): string[] {
  return segments.map(segmentLabel)
}

function segmentDescription(segments: TitleSegment[]): string {
  const lastSegment = last(segments)
  if (typeof lastSegment === 'string') return ''
  return lastSegment?.description || ''
}

/**
 * Hook to detect if a container is overflowing and needs truncation.
 * Uses ResizeObserver to respond to size changes.
 */
function useOverflowDetection(containerRef: React.RefObject<HTMLElement | null>) {
  const [isOverflowing, setIsOverflowing] = useState(false)
  const checkTimeoutRef = useRef<NodeJS.Timeout>(undefined)

  useLayoutEffect(() => {
    const element = containerRef.current
    if (!element) return

    const checkOverflow = () => {
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current)
      }

      checkTimeoutRef.current = setTimeout(() => {
        if (!containerRef.current) return
        const overflowing =
          containerRef.current.scrollWidth > containerRef.current.clientWidth
        setIsOverflowing((prev) => (prev !== overflowing ? overflowing : prev))
      }, 0)
    }

    checkOverflow()

    const resizeObserver = new ResizeObserver(() => {
      checkOverflow()
    })

    resizeObserver.observe(element)

    return () => {
      resizeObserver.disconnect()
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current)
      }
    }
  }, [containerRef])

  return isOverflowing
}

/** Collapsed breadcrumb dropdown that shows hidden middle segments */
function CollapsedSegments({ segments }: { segments: TitleSegment[] }) {
  if (segments.length === 0) return null

  return (
    <m.li
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      className="relative inline-flex min-w-12 shrink-0 gap-2"
      key="collapsed-segments"
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="text-accent-foreground hover:text-foreground transition-colors"
          >
            ...
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {segments.map((segment, index) => {
            const label = segmentLabel(segment)
            const onClick = typeof segment === 'object' ? segment.onClick : undefined
            return (
              <DropdownMenuItem
                key={`${label}.${index}`}
                onClick={onClick}
                disabled={!onClick}
              >
                {label}
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      <div className="text-accent-foreground">/</div>
    </m.li>
  )
}

export const TitleContext = createContext<{
  titles: TitleSegment[]
  registerTitle: (id: string, title: TitleSegment[]) => void
  unregisterTitle: (id: string) => void
  actionsSlot: HTMLElement | null
  historyRef: React.RefObject<string[]>
  goBack: () => void
}>({
  titles: [],
  registerTitle: VOID_FUNC,
  unregisterTitle: VOID_FUNC,
  actionsSlot: null,
  historyRef: { current: [] },
  goBack: VOID_FUNC,
})

/** Internal context to pass setActionsSlot from provider to SettingsTitle */
const ActionsSlotSetterContext = createContext<{
  setActionsSlot: (el: HTMLElement | null) => void
}>({
  setActionsSlot: VOID_FUNC,
})

export const useSettingTitle = (title: TitleSegment[]) => {
  const { registerTitle, unregisterTitle } = use(TitleContext)
  const id = useId()
  const labels = useMemo(() => segmentLabels(title), [title])
  const previousLabels = usePrevious(labels)

  useEffect(() => {
    return () => {
      unregisterTitle(id)
    }
  }, [id, unregisterTitle])

  useEffect(() => {
    if (isShallowEqual(previousLabels, labels)) return

    registerTitle(id, title)
  }, [id, labels, previousLabels, registerTitle, title])
}

/**
 * Portal component for rendering actions in the settings title header.
 * Children are portaled into a slot in the SettingsTitle component.
 *
 * Usage:
 * ```tsx
 * <SettingsActions>
 *   <Button onClick={handleCreate}>Create</Button>
 * </SettingsActions>
 * ```
 */
export function SettingsActions({ children }: PropsWithChildren) {
  const { actionsSlot } = use(TitleContext)

  if (!actionsSlot) return null
  return createPortal(children, actionsSlot)
}

export function SettingsTitleProvider({
  children,
  initialTitle,
}: PropsWithChildren<{ initialTitle?: string }>) {
  const [titleStack, setTitleStack] = useState<
    Array<{ id: string; title: TitleSegment[] }>
  >([])
  const [actionsSlot, setActionsSlot] = useState<HTMLElement | null>(null)
  const titles =
    titleStack[titleStack.length - 1]?.title ?? (initialTitle ? [initialTitle] : [])
  const labels = segmentLabels(titles)
  useTitle(`Settings / ${labels.join(' / ')}`)

  // History tracking for back navigation
  const historyRef = useRef<string[]>([])
  const isGoingBackRef = useRef(false)
  const location = useLocation()
  const navigate = useNavigate({ from: '/$alias/settings' })
  const previousPath = usePrevious(location.pathname)

  // Track history when settings route changes (skip during back navigation)
  useEffect(() => {
    if (isGoingBackRef.current) {
      isGoingBackRef.current = false
      return
    }
    if (
      previousPath &&
      previousPath !== location.pathname &&
      previousPath.includes('/settings/')
    ) {
      historyRef.current = [...historyRef.current, previousPath]
    }
  }, [location.pathname, previousPath])

  const goBack = useCallback(() => {
    const history = historyRef.current
    if (history.length > 0) {
      isGoingBackRef.current = true
      const previous = history[history.length - 1]
      historyRef.current = history.slice(0, -1)
      navigate({ to: previous })
    }
  }, [navigate])

  const registerTitle = useCallback((id: string, title: TitleSegment[]) => {
    setTitleStack((stack) => {
      const existingIndex = stack.findIndex((entry) => entry.id === id)

      if (existingIndex === -1) {
        return [...stack, { id, title }]
      }

      const nextStack = stack.filter((entry) => entry.id !== id)
      return [...nextStack, { id, title }]
    })
  }, [])

  const unregisterTitle = useCallback((id: string) => {
    setTitleStack((stack) => stack.filter((entry) => entry.id !== id))
  }, [])

  return (
    <TitleContext
      value={{
        titles,
        registerTitle,
        unregisterTitle,
        actionsSlot,
        historyRef,
        goBack,
      }}
    >
      <ActionsSlotSetterContext value={{ setActionsSlot }}>
        {children}
      </ActionsSlotSetterContext>
    </TitleContext>
  )
}

export function SettingsTitle(props: PropsWithChildren) {
  const { titles, historyRef, goBack } = use(TitleContext)
  const { setActionsSlot } = use(ActionsSlotSetterContext)
  const labels = segmentLabels(titles)
  const description = segmentDescription(titles)
  useTitle(`Settings / ${labels.join(' / ')}`)

  // Container ref for overflow detection
  const containerRef = useRef<HTMLOListElement>(null)
  const isOverflowing = useOverflowDetection(containerRef)

  // Determine which segments to show vs collapse
  // Only collapse if overflowing AND there are more than 2 segments
  const shouldCollapse = isOverflowing && titles.length > 2

  // Compute visible segments based on collapse state
  const firstSegment = titles[0]
  const lastSegment = titles[titles.length - 1]
  const collapsedSegments = titles.slice(1, -1)

  // Handle back navigation - use history if available, otherwise parent segment
  const handleBack = useCallback(() => {
    if (historyRef.current.length > 0) {
      goBack()
      return
    }

    // Fallback: navigate to parent section
    const parentSegment = titles[0]
    if (typeof parentSegment === 'object' && parentSegment.onClick) {
      parentSegment.onClick()
    }
  }, [historyRef, goBack, titles])

  return (
    <div className="gutters my-8">
      <header className="flex items-center justify-between gap-4">
        <hgroup className="min-w-0 flex-1">
          <Heading1 className="mb-1 flex min-w-0 items-center space-x-1.5">
            <div
              className={cn(
                '-ml-2 w-0 scale-95 overflow-hidden opacity-0 transition-all',
                {
                  'ml-0 w-10 scale-100 opacity-100': titles.length > 1,
                },
              )}
            >
              <Button
                size="icon-sm"
                variant="outline"
                icon="arrow-left"
                className="mr-3"
                onClick={handleBack}
              />
            </div>
            <span className="text-accent-foreground shrink-0">Settings</span>
            <span className="text-accent-foreground shrink-0">/</span>
            <LayoutGroup>
              <m.ol
                ref={containerRef}
                className="relative inline-flex min-w-0 shrink gap-2 overflow-hidden"
              >
                <AnimatePresence initial={false} mode="popLayout">
                  {shouldCollapse ? (
                    // Collapsed mode: first + "..." dropdown + last
                    <>
                      {/* First segment */}
                      {firstSegment && (
                        <m.li
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="relative inline-flex min-w-12 shrink-0 gap-2"
                          key={`first.${segmentLabel(firstSegment)}`}
                        >
                          <div className="relative whitespace-nowrap">
                            {typeof firstSegment === 'object' && firstSegment.onClick ? (
                              <button
                                type="button"
                                onClick={firstSegment.onClick}
                                className="text-accent-foreground hover:text-foreground transition-colors"
                              >
                                {segmentLabel(firstSegment)}
                              </button>
                            ) : (
                              segmentLabel(firstSegment)
                            )}
                          </div>
                          <div className="text-accent-foreground">/</div>
                        </m.li>
                      )}

                      {/* Collapsed middle segments dropdown */}
                      <CollapsedSegments segments={collapsedSegments} />

                      {/* Last segment */}
                      {lastSegment && (
                        <m.li
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="relative inline-flex min-w-[40px] shrink-0 gap-2"
                          key={`last.${segmentLabel(lastSegment)}`}
                        >
                          <div className="relative whitespace-nowrap">
                            {typeof lastSegment === 'object' && lastSegment.onClick ? (
                              <button
                                type="button"
                                onClick={lastSegment.onClick}
                                className="text-accent-foreground hover:text-foreground transition-colors"
                              >
                                {segmentLabel(lastSegment)}
                              </button>
                            ) : (
                              segmentLabel(lastSegment)
                            )}
                          </div>
                        </m.li>
                      )}
                    </>
                  ) : (
                    // Show all segments when not collapsed
                    titles.map((segment, index) => {
                      const label = segmentLabel(segment)
                      const onClick =
                        typeof segment === 'object' ? segment.onClick : undefined
                      const isClickable = !!onClick
                      const isLast = index === titles.length - 1

                      return (
                        <m.li
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="relative inline-flex min-w-[40px] shrink-0 gap-2"
                          key={`${label}.${index}`}
                        >
                          <div className="relative whitespace-nowrap">
                            {isClickable ? (
                              <button
                                type="button"
                                onClick={onClick}
                                className="text-accent-foreground hover:text-foreground transition-colors"
                              >
                                {label}
                              </button>
                            ) : (
                              label
                            )}
                          </div>
                          {!isLast && <div className="text-accent-foreground">/</div>}
                        </m.li>
                      )
                    })
                  )}
                </AnimatePresence>
              </m.ol>
            </LayoutGroup>
          </Heading1>

          <p className="text-accent-foreground">{description}</p>
        </hgroup>
        {/* Actions slot - content from <SettingsActions> portals here */}
        <div
          ref={setActionsSlot}
          className="flex shrink-0 items-center gap-2 empty:hidden"
        />
      </header>
      {props.children}
    </div>
  )
}
